import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Star,
  Quote,
  MapPin,
  Calendar,
  Shield,
  CheckCircle,
  Filter,
  ThumbsUp,
  Home,
  CloudLightning,
  Wrench,
  FileText,
  Play,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

const TESTIMONIALS = [
  {
    id: 1,
    name: "Michael & Sarah Johnson",
    location: "Minneapolis",
    date: "2024-07",
    rating: 5,
    category: "storm-damage",
    title: "Incredible service after the July hailstorm",
    content: "After the major hailstorm hit our neighborhood, we were overwhelmed with the damage. StormCareMN connected us with a contractor who was professional, thorough, and helped us navigate the entire insurance claim process. Our new roof looks amazing and the whole experience was stress-free.",
    service: "Hail Damage Restoration",
    verified: true
  },
  {
    id: 2,
    name: "Robert Chen",
    location: "Eden Prairie",
    date: "2024-06",
    rating: 5,
    category: "roof-replacement",
    title: "Best decision we made for our home",
    content: "We knew our 25-year-old roof needed replacement but didn't know where to start. The contractor StormCareMN recommended was fantastic - fair pricing, excellent communication, and completed the job in just two days. Highly recommend their service!",
    service: "Full Roof Replacement",
    verified: true
  },
  {
    id: 3,
    name: "Jennifer Martinez",
    location: "Bloomington",
    date: "2024-06",
    rating: 5,
    category: "insurance-claim",
    title: "Insurance claim made easy",
    content: "I was nervous about dealing with my insurance company for storm damage. The contractor I was connected with met with the adjuster, documented everything properly, and made sure nothing was missed. My claim was approved for the full replacement. So grateful for the guidance!",
    service: "Insurance Claim Assistance",
    verified: true
  },
  {
    id: 4,
    name: "David & Lisa Thompson",
    location: "Plymouth",
    date: "2024-05",
    rating: 5,
    category: "storm-damage",
    title: "Quick response when we needed it most",
    content: "Wind damage from a spring storm left us with missing shingles and a leak. Within hours of submitting our information, we had a contractor on-site assessing the damage. They tarped the roof immediately and scheduled repairs. True professionals.",
    service: "Wind Damage Repair",
    verified: true
  },
  {
    id: 5,
    name: "Amanda Wilson",
    location: "Maple Grove",
    date: "2024-05",
    rating: 5,
    category: "roof-replacement",
    title: "Transparent process from start to finish",
    content: "What I appreciated most was the honesty. The contractor didn't try to oversell or pressure us. They explained all our options, showed us material samples, and gave us time to decide. The installation crew was respectful of our property and cleaned up perfectly.",
    service: "Architectural Shingle Roof",
    verified: true
  },
  {
    id: 6,
    name: "James & Karen O'Brien",
    location: "St. Paul",
    date: "2024-04",
    rating: 5,
    category: "insurance-claim",
    title: "Saved us thousands on our claim",
    content: "Our insurance initially offered a lowball estimate. The contractor StormCareMN connected us with documented additional damage the adjuster missed and successfully got our claim supplemented. We ended up with proper coverage for a full replacement.",
    service: "Insurance Supplemental Claim",
    verified: true
  },
  {
    id: 7,
    name: "Patricia Anderson",
    location: "Eagan",
    date: "2024-04",
    rating: 5,
    category: "storm-damage",
    title: "Professional and thorough",
    content: "Hail damage isn't always visible from the ground, but the inspection revealed significant damage. The contractor took detailed photos, explained everything clearly, and worked directly with our insurance. Couldn't have asked for a better experience.",
    service: "Hail Damage Assessment",
    verified: true
  },
  {
    id: 8,
    name: "Thomas & Maria Garcia",
    location: "Burnsville",
    date: "2024-03",
    rating: 5,
    category: "roof-replacement",
    title: "Worth every penny",
    content: "We chose impact-resistant shingles based on the contractor's recommendation. The installation was flawless, and we even got a discount on our homeowners insurance for the upgraded materials. Great investment for Minnesota weather!",
    service: "Impact-Resistant Roof",
    verified: true
  },
  {
    id: 9,
    name: "Susan Miller",
    location: "Woodbury",
    date: "2024-03",
    rating: 5,
    category: "storm-damage",
    title: "Fast, efficient, professional",
    content: "From the initial call to the completed repair, everything was handled professionally. The crew arrived on time, worked efficiently, and left our property cleaner than they found it. Excellent service all around.",
    service: "Storm Damage Repair",
    verified: true
  },
  {
    id: 10,
    name: "William & Nancy Taylor",
    location: "Lakeville",
    date: "2024-02",
    rating: 5,
    category: "insurance-claim",
    title: "Advocates for homeowners",
    content: "Having someone who understands the insurance process on your side makes all the difference. The contractor documented everything thoroughly and communicated directly with our adjuster. No stress, no surprises, just results.",
    service: "Insurance Claim Support",
    verified: true
  },
  {
    id: 11,
    name: "Richard & Barbara Nelson",
    location: "Roseville",
    date: "2024-02",
    rating: 5,
    category: "roof-replacement",
    title: "Exceeded our expectations",
    content: "We interviewed three contractors before choosing the one StormCareMN recommended. Best decision we made. Quality workmanship, fair pricing, and they stood behind their warranty when we had a minor issue months later.",
    service: "Complete Roof System",
    verified: true
  },
  {
    id: 12,
    name: "Elizabeth Foster",
    location: "Minnetonka",
    date: "2024-01",
    rating: 5,
    category: "storm-damage",
    title: "Peace of mind restored",
    content: "After years of worrying about our aging roof during every storm, we finally have peace of mind. The new roof handles Minnesota weather beautifully, and the whole process was easier than I expected. Thank you!",
    service: "Full Roof Replacement",
    verified: true
  }
];

const STATS = [
  { value: "500+", label: "Homeowners Helped" },
  { value: "4.9", label: "Average Rating" },
  { value: "98%", label: "Would Recommend" },
  { value: "73", label: "Cities Served" }
];

const CATEGORIES = [
  { value: "all", label: "All Reviews", icon: Star },
  { value: "storm-damage", label: "Storm Damage", icon: CloudLightning },
  { value: "roof-replacement", label: "Roof Replacement", icon: Home },
  { value: "insurance-claim", label: "Insurance Claims", icon: FileText }
];

export default function Reviews() {
  useSEO();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCount, setVisibleCount] = useState(6);

  const filteredTestimonials = selectedCategory === "all" 
    ? TESTIMONIALS 
    : TESTIMONIALS.filter(t => t.category === selectedCategory);

  const visibleTestimonials = filteredTestimonials.slice(0, visibleCount);
  const hasMore = visibleCount < filteredTestimonials.length;

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "StormCareMN",
    "description": "Minnesota storm damage and roofing contractor referral service",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": TESTIMONIALS.length.toString(),
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": TESTIMONIALS.slice(0, 5).map(t => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "datePublished": t.date,
      "reviewBody": t.content,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating.toString(),
        "bestRating": "5"
      }
    }))
  };

  return (
    <div data-testid="reviews-page" className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />

      {/* Hero Section */}
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-gray-400 text-sm mb-6">
            <Link to="/" className="hover:text-orange">Home</Link>
            <span>/</span>
            <span className="text-gray-300">Reviews</span>
          </nav>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 fill-current" />
              Verified Testimonials
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white mb-4">
              What Minnesota Homeowners Say
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              Real stories from homeowners we have helped navigate storm damage, roof replacement, 
              and insurance claims across the Twin Cities.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {STATS.map((stat) => (
                <div key={stat.label} className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-heading font-bold text-orange mb-1">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => {
                  setSelectedCategory(cat.value);
                  setVisibleCount(6);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === cat.value
                    ? 'bg-orange text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleTestimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                    <h3 className="font-heading font-semibold text-navy">{testimonial.title}</h3>
                  </div>
                  {testimonial.verified && (
                    <div className="flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="relative mb-4">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-gray-100" />
                  <p className="text-gray-600 text-sm leading-relaxed relative z-10">
                    {testimonial.content}
                  </p>
                </div>

                {/* Service Tag */}
                <div className="mb-4">
                  <span className="inline-flex items-center gap-1 bg-navy/5 text-navy text-xs px-3 py-1 rounded-full">
                    <Wrench className="w-3 h-3" />
                    {testimonial.service}
                  </span>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <div className="font-medium text-navy text-sm">{testimonial.name}</div>
                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                      <MapPin className="w-3 h-3" />
                      {testimonial.location}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400 text-xs">
                    <Calendar className="w-3 h-3" />
                    {new Date(testimonial.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-10">
              <Button
                variant="outline"
                onClick={() => setVisibleCount(prev => prev + 6)}
                className="border-gray-300"
              >
                Load More Reviews
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Why Homeowners Trust Us
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We connect Minnesota homeowners with vetted, reputable roofing professionals 
              who deliver quality work and honest service.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Vetted Contractors",
                description: "Every contractor in our network is licensed, insured, and has a proven track record in Minnesota."
              },
              {
                icon: ThumbsUp,
                title: "No Pressure, No Obligation",
                description: "Get honest assessments and recommendations without high-pressure sales tactics."
              },
              {
                icon: CheckCircle,
                title: "Insurance Expertise",
                description: "Contractors who understand insurance claims help ensure you get fair coverage for legitimate damage."
              }
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-orange" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-semibold text-white mb-4">
            Ready to Join Our Satisfied Customers?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Whether you have storm damage, need a roof replacement, or want a free inspection, 
            we will connect you with trusted professionals who deliver results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-orange hover:bg-orange-600 text-white">
              <Link to="/">
                Get Free Assessment
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-gray-600 text-white hover:bg-white/10">
              <Link to="/roof-assessment">
                Take Roof Quiz
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Location Reviews */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-semibold text-navy mb-8 text-center">
            Reviews by Location
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {['Minneapolis', 'St. Paul', 'Eden Prairie', 'Plymouth', 'Bloomington', 'Maple Grove', 'Woodbury', 'Eagan', 'Burnsville', 'Lakeville', 'Roseville', 'Minnetonka'].map((city) => (
              <Link 
                key={city}
                to={`/service-areas/${city.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-gray-100 hover:bg-orange/10 hover:text-orange px-4 py-2 rounded-full text-sm text-gray-700 transition-colors"
              >
                {city} Reviews
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
